// Class names and small pieces shared by the redesigned pages.
import { ReactNode } from "react";
import clsx from "clsx";
import { addToast } from "../toast/toast.service";
import { useT } from "@app/contexts/preferences.context";
import { IcCopy, IcLinux, IcMonitor, IcWindows } from "../icons";

/* ---------- buttons / inputs ---------- */
export const btn =
  "inline-flex items-center justify-center gap-2 h-[38px] px-3.5 rounded-[9px] border border-transparent font-semibold text-[13.5px] whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
export const btnPrimary = "bg-brand text-brand-ink hover:bg-brand-hover";
export const btnGhost = "bg-surface border-line text-fg hover:border-line-2 hover:bg-surface-2";
export const btnSoft = "bg-brand-soft text-brand";
export const btnPlay = "bg-play-soft text-play hover:bg-play hover:text-white";
export const btnSm = "!h-8 !px-2.5 !text-[12.5px] !rounded-lg";
export const btnLg = "!h-11 !text-[15px] !rounded-[11px]";

export const iconBtn =
  "relative grid place-items-center w-[38px] h-[38px] rounded-[10px] border border-line bg-surface text-fg hover:border-line-2 disabled:opacity-45 disabled:cursor-not-allowed";
export const miniBtn =
  "grid place-items-center w-[26px] h-[26px] rounded-md text-faint hover:bg-surface-2 hover:text-fg";

export const input =
  "h-9 w-full rounded-[10px] border border-line bg-surface px-3 text-fg outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--brand-soft)]";
export const select =
  "pcr-select h-10 appearance-none rounded-[10px] border border-line bg-surface pl-3 pr-8 font-medium text-fg";

/* ---------- PC tower picture used on cards ---------- */
export function PcGlyph({ height = 80, muted, color }: { height?: number; muted?: boolean; color?: string }) {
  // color: màu danh mục (khung và đèn vỏ case cùng màu chữ danh mục)
  const glow = muted ? "var(--faint)" : color || "var(--brand)";

  return (
    <svg viewBox="0 0 80 100" style={{ height, width: "auto" }} aria-hidden="true">
      <rect x="14" y="6" width="52" height="88" rx="5" fill="#0B0F1A" stroke="#2A3245" strokeWidth="1.5" />
      <rect x="19" y="11" width="32" height="78" rx="3" fill="#121826" stroke={glow} strokeOpacity=".55" />
      <circle cx="35" cy="28" r="9" fill="none" stroke={glow} strokeWidth="2.2" />
      <circle cx="35" cy="28" r="2.5" fill={glow} />
      <circle cx="35" cy="50" r="9" fill="none" stroke={glow} strokeWidth="2.2" />
      <circle cx="35" cy="50" r="2.5" fill={glow} />
      <circle cx="35" cy="72" r="9" fill="none" stroke={glow} strokeWidth="2.2" strokeOpacity=".6" />
      <rect x="55" y="14" width="6" height="2" rx="1" fill={glow} />
      <circle cx="58" cy="84" r="2" fill={glow} />
    </svg>
  );
}

/* ---------- vòng xoay nhỏ trong nút đang chạy thao tác (useBusy) ---------- */
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={clsx("inline-block h-4 w-4 flex-none animate-spin rounded-full border-2 border-current border-t-transparent", className)}
    />
  );
}

/* ---------- thông số cấu hình có dấu "/" (Package, Cloud, VPS) ---------- */
/** Tách thông số theo dấu "/": "Dual xeon 2680v4 / 22 nhân - 64 luồng" => ["Dual xeon 2680v4", "22 nhân - 64 luồng"] */
export const splitSpec = (value?: string | number | null) =>
  String(value ?? "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);

/** Phần chính của thông số (trước dấu "/"), dùng ở các dòng tóm tắt 1 dòng */
export const specMain = (value?: string | number | null) => splitSpec(value)[0] || String(value ?? "");

/** Thông số: phần sau dấu "/" xuống dòng, chữ nhỏ, như lời giải thích cho phần chính */
export function SpecText({ value }: { value?: string | number | null }) {
  const [main, ...notes] = splitSpec(value);

  if (!notes.length) return <>{value}</>;

  return (
    <>
      {main}
      {notes.map((note, i) => (
        <small key={i} className="block text-[11px] font-normal leading-snug text-muted">
          {note}
        </small>
      ))}
    </>
  );
}

/* ---------- hệ điều hành của Package (admin tự nhập) ---------- */
/** Icon theo tên OS: Windows, Linux (Ubuntu, Debian…), còn lại là màn hình */
export function OsIcon({ os, className }: { os?: string; className?: string }) {
  const name = String(os ?? "").toLowerCase();
  const Icon = /win/.test(name)
    ? IcWindows
    : /linux|ubuntu|debian|centos|fedora|rocky|alma/.test(name)
      ? IcLinux
      : IcMonitor;

  return <Icon className={clsx("flex-none", className)} aria-hidden="true" />;
}

/* ---------- đang tải (thay cho "Chưa có…" khi dữ liệu chưa về) ---------- */
export function LoadingBlock({ className }: { className?: string }) {
  const t = useT();

  return (
    <div className={clsx("flex items-center justify-center gap-2 px-4 py-12 text-[13px] text-muted", className)}>
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-brand" />
      {t("Đang tải…")}
    </div>
  );
}

/* ---------- copy to clipboard ---------- */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const t = useT();

  return (
    <button
      type="button"
      className={miniBtn}
      aria-label={t(label)}
      title={t(label)}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value).then(() => addToast({ text: t("Đã sao chép") }));
      }}
    >
      <IcCopy className="h-[15px] w-[15px]" />
    </button>
  );
}

/* ---------- status pill ---------- */
export function StatusPill({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "bad" | "muted";
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-[12.5px] font-semibold before:h-[7px] before:w-[7px] before:rounded-full before:bg-current",
        {
          "bg-ok-soft text-ok": tone === "ok",
          "bg-warn-soft text-warn before:animate-pulse": tone === "warn",
          "bg-bad-soft text-bad": tone === "bad",
          "bg-surface-2 text-muted": tone === "muted",
        }
      )}
    >
      {children}
    </span>
  );
}
